import React from 'react';
import { PropTypes } from 'prop-types';
import { Input, Radio } from 'antd';
import './input.style.css';
import ReactQuill from 'react-quill';
import quillTable from 'quill-table';
import 'react-quill/dist/quill.snow.css';

import ImageResize from 'quill-image-resize-module-react';
import Quill from 'quill';

Quill.register('modules/imageResize', ImageResize);
Quill.register(quillTable.TableCell);
Quill.register(quillTable.TableRow);
Quill.register(quillTable.Table);
Quill.register(quillTable.Contain);
Quill.register('modules/table', quillTable.TableModule);

const maxRows = 10;
const maxCols = 5;
const tableOptions = [];
for (let r = 1; r <= maxRows; r++) {
  for (let c = 1; c <= maxCols; c++) {
    tableOptions.push('newtable_' + r + '_' + c);
  }
}

export const INPUT_TYPE = {
  text: 'text',
  number: 'number',
  editor: 'editor',
  textarea: 'textarea',
  radio: 'radio',
  search: 'search',
  date: 'date',
  file: 'file',
  password: 'password',
};

const FormInput = props => {
  const {
    label,
    type = INPUT_TYPE.text,
    name,
    isDisabled = false,
    isRequired = true,
    id,
    value,
    style,
    errorMsg,
    helperText,
    className,
    handleChange,
    ...rest
  } = props;

  function renderField() {
    switch (type) {
      case INPUT_TYPE.editor:
        return (
          <ReactQuill
            theme="snow"
            name={name}
            value={rest.editorState || ''}
            onChange={rest.handleEditorStateChange}
            placeholder={rest.placeholder}
            className="quill-editor notranslate"
            modules={{
              table: true,
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                ['link', 'image'],
                [{ table: tableOptions }, { table: 'append-row' }, { table: 'append-col' }],
                [{ align: [] }],
                ['clean'],
              ],
              imageResize: {
                modules: ['Resize', 'DisplaySize'],
              },
            }}
          />
        );

      case INPUT_TYPE.textarea:
        return (
          <Input.TextArea
            name={name}
            type={type}
            value={value}
            onChange={handleChange}
            rows={rest?.rows ?? 2}
            placeholder={rest.placeholder || `Please enter ${label?.toLowerCase()}`}
            disabled={isDisabled}
          />
        );

      case INPUT_TYPE.search:
        return (
          <Input.Search
            name={name}
            type={type}
            value={value}
            onChange={handleChange}
            placeholder={rest.placeholder || `Please enter ${label?.toLowerCase()}`}
            disabled={isDisabled}
            enterButton
            onSearch={rest?.handleSearch}
          />
        );

      case INPUT_TYPE.radio:
        return (
          <Radio.Group
            name={name}
            value={value}
            disabled={isDisabled}
            onChange={handleChange}
            defaultValue={rest.defaultValue}
          >
            {rest.radioOptions &&
              rest.radioOptions.map(radioOpt => <Radio value={radioOpt?.value}>{radioOpt?.label}</Radio>)}
          </Radio.Group>
        );

      case INPUT_TYPE.date:
        return (
          <Input
            name={name}
            type="date"
            value={value}
            onChange={handleChange}
            placeholder={rest.placeholder || `Please enter ${label?.toLowerCase()}`}
            disabled={isDisabled}
          />
        );

      case INPUT_TYPE.password:
        return (
          <Input.Password
            name={name}
            value={value}
            onChange={handleChange}
            placeholder={rest.placeholder || `Please enter ${label?.toLowerCase()}`}
            disabled={isDisabled}
            {...rest}
          />
        );

      default:
        return (
          <Input
            name={name}
            type={type}
            value={value}
            onChange={handleChange}
            placeholder={rest.placeholder || `Please enter ${label?.toLowerCase()}`}
            disabled={isDisabled}
            suffix={rest.suffix ?? null}
            {...rest}
          />
        );
    }
  }

  return (
    <div className={isRequired ? 'input-required' : 'form-input'}>
      <div className="input-label">
        <label htmlFor={id ?? name}>{label}</label>
      </div>
      <div className={`${rest.errors?.[`${name}`] ? 'fieldError' : ''}`}>{rest.children ?? renderField()}</div>
      {rest?.errors && rest?.touched && rest.touched?.[`${name}`] && rest.errors?.[`${name}`] && (
        <div className="input-required-msg">
          <span>{rest.errors?.[`${name}`]}</span>
        </div>
      )}
    </div>
  );
};

export default FormInput;

FormInput.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  type: PropTypes.string,
  name: PropTypes.string,
  isDisabled: PropTypes.bool,
  isRequired: PropTypes.bool,
  value: PropTypes.string,
  style: PropTypes.string,
  errorMsg: PropTypes.string,
  helperText: PropTypes.string,
  className: PropTypes.string,
  handleChange: PropTypes.func,
};
